import { Message } from "primereact/message";
import React, { useMemo } from "react";
import styled from "styled-components";
import { device } from "../../constants/mediaQueries";
import { CvpCard } from "../../generic-components/cards/CvpCard";
import { isAppTypeIframe } from "../../getAppType";
import { useLocStore } from "../../localization";
import { LocContext } from "../../localization/LocContext";
import {
  getApplyRedirectProps,
  getJobRedirectProps,
} from "../../url/getJobRedirectProps";
import { optimizeImageUrl } from "../../url/synchronizeCacheBusterParameter";
import { parseJobHtml } from "../../utils/parseJobHtml";
import { useDevice } from "../../utils/style/useDevice";
import { ApplyWithoutProfileConsentForm } from "../forms/apply/applyWithoutProfile/ApplyWithoutProfileConsentForm";
import { useApplyWithoutProfileFormData } from "../forms/apply/applyWithoutProfile/useApplyWithoutProfileFormData";
import { PageLayout } from "../layout/PageLayout";
import { CvpCardBreadCrumb } from "../nav/embed/CvpCardBreadCrumb";
import { CustomMenuItem } from "../nav/MenuItems/completeMenuItem";
import {
  StyledAppliedMessage,
  StyledHeading,
  StyledSubHeading,
} from "./ApplySharedStyles";
import { useSettings } from "../../settings/useSettings";
import { observer } from "mobx-react-lite";
import { useDeviceClassNames } from "../../utils/useDeviceClassNames";

export const ApplyWithoutProfile = observer(() => {
  const {
    idRelatedResolved,
    isCandidateAttachedToCase,
    isFetched,
    job,
    typeRelatedResolved,
  } = useApplyWithoutProfileFormData();
  const { isMobile } = useDevice();
  const settings = useSettings();

  const L = useLocStore(LocContext.GENERAL);
  const deviceClassNames = useDeviceClassNames();

  const heading = useMemo(() => {
    return parseJobHtml(job?.jobText?.heading ?? "");
  }, [job?.jobText?.heading]);

  if (!isFetched) return null;

  const logoUrl = optimizeImageUrl(job.job.urlLogo);

  const breadcrumbItems: CustomMenuItem[] = [
    {
      label: L.id("4154ecd1fcd8").loc("Ledige stillinger"),
      isSelected: () => false,
      path: "jobs",
    },
    {
      label:
        job.jobText.jobtitle ||
        job.job.positionTypeFreeText ||
        job.job.positionType,
      isSelected: () => false,
      ...getJobRedirectProps(idRelatedResolved, typeRelatedResolved),
    },
    {
      label: L.id("f18045324d32").loc("Søk på stilling"),
      isSelected: () => true,
      ...getApplyRedirectProps(idRelatedResolved, typeRelatedResolved),
    },
  ];

  if (isCandidateAttachedToCase) {
    return (
      <PageLayout extraPadding>
        <StyledCvpCard
          scollcard={!isMobile && !isAppTypeIframe()}
          className={deviceClassNames}
          noPaddingTop
          border
        >
          <div className="apply-breadcrumb">
            <CvpCardBreadCrumb
              breadcrumbItems={breadcrumbItems}
              className="cvp-card-breadcrumb"
              embedProps={{ showBreadCrumb: true }}
              noPadding
            />
          </div>
          <div className="apply-container">
            <div className="apply-header">
              <StyledHeading>
                <h1 className="cvp-h1">
                  {L.id("1a03d0796593").loc("Søk på stilling")}
                </h1>
                {!!logoUrl && <img src={logoUrl} key={logoUrl} />}
              </StyledHeading>
              <StyledSubHeading>
                <h2 className="cvp-h2">{heading}</h2>
              </StyledSubHeading>
            </div>
            <StyledAppliedMessage>
              <Message
                severity="info"
                text={L.id("cb6bc81f35c4").loc(
                  "Du har allerede søkt på denne stillingen"
                )}
              />
            </StyledAppliedMessage>
          </div>
        </StyledCvpCard>
      </PageLayout>
    );
  }

  return (
    <PageLayout extraPadding>
      <StyledCvpCard
        scollcard={!isMobile && !isAppTypeIframe()}
        className={deviceClassNames}
        noPaddingTop
        border
      >
        <div className="apply-breadcrumb">
          <CvpCardBreadCrumb
            breadcrumbItems={breadcrumbItems}
            className="cvp-card-breadcrumb"
            embedProps={{ showBreadCrumb: true }}
            noPadding
          />
        </div>
        <div className="apply-container">
          <div className="apply-header">
            <StyledHeading>
              <h1 className="cvp-h1">
                {L.id("179fd124d546").loc("Søk på stilling")}
              </h1>
              {!!logoUrl && <img src={logoUrl} key={logoUrl} />}
            </StyledHeading>
            <StyledSubHeading>
              <h2 className="cvp-h2">{heading}</h2>
            </StyledSubHeading>
          </div>
          {!settings.jobview_requireCoverLetter &&
            !settings.jobview_requireCv && (
              <h4 className="cvp-h4">
                {L.id("21411e9d72f0").loc(
                  "Du må samtykke og velge en av alternativene under for å søke:"
                )}
              </h4>
            )}

          {(settings.jobview_requireCoverLetter ||
            settings.jobview_requireCv) && (
            <h4 className="cvp-h4">
              {settings.jobview_requireCoverLetter &&
                settings.jobview_requireCv &&
                L.id("443529d967c7").loc(
                  "Du må skrive en søknadstekst, legge ved CV, samtykke og velge en av alternativene under for å søke:"
                )}

              {settings.jobview_requireCoverLetter &&
                !settings.jobview_requireCv &&
                L.id("da7726716f6c").loc(
                  "Du må skrive en søknadstekst, samtykke og velge en av alternativene under for å søke:"
                )}

              {!settings.jobview_requireCoverLetter &&
                settings.jobview_requireCv &&
                L.id("8156bbe87af0").loc(
                  "Du må legge ved CV, samtykke og velge en av alternativene under for å søke:"
                )}
            </h4>
          )}

          <ApplyWithoutProfileConsentForm
            requireCoverLetter={settings.jobview_requireCoverLetter}
            requireCv={settings.jobview_requireCv}
          />
        </div>
      </StyledCvpCard>
    </PageLayout>
  );
});

export default ApplyWithoutProfile;

const StyledCvpCard = styled(CvpCard)`
  display: flex;
  flex-direction: column;

  .p-timeline-event .p-card {
    padding: 1rem;

    @media ${device.md} {
      padding: 1.5rem;
    }
  }

  &:not(.cvp-iframe):not(.cvp-mobile) {
    .apply-breadcrumb::before {
      /* Hide overflow when scrolling */
      content: "";
      position: absolute;
      left: 0;
      top: -1.5rem;
      height: 1.5rem;
      width: 100%;
      background-color: var(--surface-0);
    }
  }

  .apply-breadcrumb {
    position: sticky;
    top: 0;
    background-color: var(--surface-0);
    z-index: 1;

    padding-bottom: 1rem;

    img {
      max-width: 300px;
      max-height: 120px;
      min-height: 120px; // prevent moving content when image is loaded
      object-fit: contain;
      margin-left: auto;
      margin-bottom: 1rem;
    }
    @media ${device.md} {
      padding-bottom: 1.5rem;
    }
  }

  .p-card-content {
    display: flex;
    flex-direction: column;
    padding: 0;
  }

  .apply-container {
    display: flex;
    flex-direction: column;
    gap: 2rem;
    // Add padding so that the content doesn't touch the edges
    // of the heading container when scrolling
    padding: 1.5rem 0.1rem;
    margin-bottom: 2rem;
  }

  .apply-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;
